import React, { useCallback, useEffect, useState } from 'react';

import Menu from 'design-system/Menu';
import PendencyModal from 'design-system/PendencyModal';
import StatusBadge from 'design-system/StatusBadge';
import { blockingColors } from 'design-system/tokens';
import { getOverdueBankSlips } from 'services/overdueBankSlipsService';
import {
  BankSlipRow,
  formatBoletoNumberDisplay,
  formatCompetenceLabel,
  formatDueDate,
  formatInstallmentAmount,
} from 'pages/BankSlip/utils/bankSlipDisplay';
import { useBankSlipRowActions } from 'pages/BankSlip/hooks/useBankSlipRowActions';

import { PENDENCIAS_TEXTS } from '../constants';
import { MenuTrigger, SlipActionsCell, SlipsTableWrap } from './styles';

type OverdueSlipsModalProps = {
  open: boolean;
  onClose: () => void;
};

const rowKey = (row: BankSlipRow): string =>
  `${row.invoice.invoiceId}-${row.installment.installmentId}`;

const MenuDotsIcon = (): JSX.Element => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <circle cx="8" cy="3" r="1.25" />
    <circle cx="8" cy="8" r="1.25" />
    <circle cx="8" cy="13" r="1.25" />
  </svg>
);

const OverdueSlipsModal = ({
  open,
  onClose,
}: OverdueSlipsModalProps): JSX.Element => {
  const [rows, setRows] = useState<BankSlipRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const { download, openEmail, openView, emailOpen, viewOpen, modals } =
    useBankSlipRowActions();

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    const result = await getOverdueBankSlips();
    setLoading(false);

    setRows(result.ok ? result.rows : []);
  }, []);

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  const toggleMenu = (key: string, isOpen: boolean): void =>
    setOpenMenu(isOpen ? key : null);

  useEffect(() => {
    if (!openMenu) return undefined;

    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest('[data-slip-menu]')) setOpenMenu(null);
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpenMenu(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openMenu]);

  return (
    <>
      <PendencyModal
        open={open && !emailOpen && !viewOpen}
        title={PENDENCIAS_TEXTS.MODAL_SLIPS_TITLE}
        onClose={onClose}
        width={780}
      >
        {!loading && rows.length > 0 && (
          <SlipsTableWrap>
            <table>
              <thead>
                <tr>
                  <th>Nº do boleto</th>
                  <th>Valor</th>
                  <th>Vencimento</th>
                  <th>Competência</th>
                  <th>Status</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const key = rowKey(row);
                  const boleto = formatBoletoNumberDisplay(
                    row.installment.typeableLine
                  );

                  const menuTrigger = (
                    <MenuTrigger
                      type="button"
                      aria-label={`Ações do boleto ${boleto.full}`}
                    >
                      <MenuDotsIcon />
                    </MenuTrigger>
                  );

                  return (
                    <tr key={key}>
                      <td title={boleto.full}>{boleto.display}</td>
                      <td>
                        {formatInstallmentAmount(row.installment, row.invoice)}
                      </td>
                      <td>{formatDueDate(row.installment.dueDate)}</td>
                      <td>
                        {formatCompetenceLabel(row.installment, row.invoice)}
                      </td>
                      <td>
                        <StatusBadge
                          variant="error"
                          backgroundColor={blockingColors.alertBg}
                          borderColor={blockingColors.alertIcon}
                          color={blockingColors.textStrong}
                        >
                          Vencido
                        </StatusBadge>
                      </td>
                      <SlipActionsCell data-slip-menu>
                        <Menu
                          open={openMenu === key}
                          onOpenChange={(isOpen) => toggleMenu(key, isOpen)}
                          align="right"
                          trigger={menuTrigger}
                          items={[
                            {
                              id: 'download',
                              label: 'Baixar 2ª via',
                              onClick: () => download(row),
                            },
                            {
                              id: 'email',
                              label: 'Enviar por e-mail',
                              onClick: () => openEmail(row),
                            },
                            {
                              id: 'view',
                              label: 'Visualizar',
                              onClick: () => openView(row),
                            },
                          ]}
                        />
                      </SlipActionsCell>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </SlipsTableWrap>
        )}
      </PendencyModal>

      {modals}
    </>
  );
};

export default OverdueSlipsModal;
